import { createContext, useMemo, type FC, type ReactNode } from "react";
import { buildSlotClaimMap, type SlotClaimMap } from "./slotClaimMap";

export type TemplateMap = Map<string, ReactNode>;

/**
 * Templating context value carries both:
 *   - `templates` — the parsed embedder template trees keyed by `<template id>`.
 *   - `claims`    — the global slot-claim map derived from those templates
 *                   (which slot id is claimed in which template). Built once
 *                   per provider render so consumers can query in O(1).
 */
export interface TemplatingContextValue {
  templates: TemplateMap;
  claims: SlotClaimMap;
}

const EMPTY: TemplatingContextValue = {
  templates: new Map(),
  claims: new Map(),
};

export const TemplatesContext = createContext<TemplatingContextValue>(EMPTY);

interface TemplatesProviderProps {
  templates: TemplateMap;
  children: ReactNode;
}

export const TemplatesProvider: FC<TemplatesProviderProps> = ({ templates, children }) => {
  const value = useMemo<TemplatingContextValue>(
    () => ({ templates, claims: buildSlotClaimMap(templates) }),
    [templates],
  );
  return <TemplatesContext.Provider value={value}>{children}</TemplatesContext.Provider>;
};
